///
/// EarthBrowser
///
/// Created by Matt Giger on December 16, 2025
/// Copyright (c) 2025 Geodata Labs
///

import { type Database } from './Database'
import { logger } from '@/utils/LogDispatcher'

/**
 * Migration definition
 */
export interface Migration {
	version: number
	name: string
	description: string
	up: string[] // Array of SQL statements to apply migration
	down?: string[] // Optional rollback statements
}

/**
 * Row shape returned from the schema_migrations table
 */
interface MigrationRow {
	version: number
	name: string
	applied_at: string
}

/**
 * Database migration manager for the browser SQLite database
 * Tracks and applies schema changes in a controlled manner
 */
export class DatabaseMigrations {
	private static readonly MIGRATIONS_TABLE = 'schema_migrations'

	/**
	 * All migrations in chronological order
	 * IMPORTANT: Never modify existing migrations, only add new ones
	 */
	private static readonly migrations: Migration[] = [
		{
			version: 1,
			name: 'locations_not_null_pop_alt',
			description: 'Rebuild locations with NOT NULL pop/alt, repairing existing NULLs to 0',
			up: [
				// Guard: ensure the table exists so the rebuild is safe even before the
				// dataset sync has created it (migrations run before LocationsTable.create).
				// For a brand-new user this creates an empty table that is rebuilt below;
				// for an existing user it is a no-op against the already-populated table.
				`create table if not exists locations (
					geoid integer primary key,
					name text not null,
					country text not null,
					admin1 text,
					admin2 text,
					pop integer,
					lon real not null,
					lat real not null,
					alt integer,
					code text,
					tz text,
					geom blob,
					h3_r3 text
				)`,

				// Drop the rtree sync triggers BEFORE dropping the table, otherwise the
				// delete trigger would wipe locations_idx as the old rows are removed.
				`drop trigger if exists locations_idx_insert`,
				`drop trigger if exists locations_idx_update`,
				`drop trigger if exists locations_idx_delete`,

				// Rebuild with the tightened schema (pop/alt NOT NULL DEFAULT 0),
				// repairing any existing NULL pop/alt to 0 during the copy.
				`create table locations_new (
					geoid integer primary key,
					name text not null,
					country text not null,
					admin1 text,
					admin2 text,
					pop integer not null default 0,
					lon real not null,
					lat real not null,
					alt integer not null default 0,
					code text,
					tz text,
					geom blob,
					h3_r3 text
				)`,
				`insert into locations_new (geoid, name, country, admin1, admin2, pop, lon, lat, alt, code, tz, geom, h3_r3)
					select geoid, name, country, admin1, admin2, coalesce(pop, 0), lon, lat, coalesce(alt, 0), code, tz, geom, h3_r3
					from locations`,
				// geoid is INTEGER PRIMARY KEY (rowid), so dropping/renaming preserves
				// rowids and the existing locations_idx entries remain valid.
				`drop table locations`,
				`alter table locations_new rename to locations`,

				// Indexes were dropped along with the old table; recreate them.
				`create index if not exists idx_locations_name on locations (name)`,
				`create index if not exists idx_locations_pop on locations (pop desc)`,
				`create index if not exists idx_locations_country_admin on locations (country, admin1)`,
				`create index if not exists idx_locations_h3_r3 on locations (h3_r3)`,

				// Ensure the spatial index exists (untouched for existing users), then
				// recreate the sync triggers we dropped above.
				`create virtual table if not exists locations_idx using rtree(
					id,
					min_x, max_x,
					min_y, max_y
				)`,
				`create trigger if not exists locations_idx_insert
				after insert on locations
				begin
					insert into locations_idx values (
						new.rowid,
						ST_XMin(new.geom),
						ST_XMax(new.geom),
						ST_YMin(new.geom),
						ST_YMax(new.geom)
					);
				end`,
				`create trigger if not exists locations_idx_update
				after update on locations
				begin
					delete from locations_idx where id = old.rowid;
					insert into locations_idx values (
						new.rowid,
						ST_XMin(new.geom),
						ST_XMax(new.geom),
						ST_YMin(new.geom),
						ST_YMax(new.geom)
					);
				end`,
				`create trigger if not exists locations_idx_delete
				after delete on locations
				begin
					delete from locations_idx where id = old.rowid;
				end`,
			],
		},
		{
			version: 2,
			name: 'buoy_stations_buoycam',
			description: 'Add buoycam flag to buoy_stations for NDBC BuoyCAM panoramas',
			up: [
				// Guard: migrations run before BuoyStationsTable.create, so ensure the
				// table exists (pre-buoycam shape) before altering it. For existing
				// users this is a no-op; the alter below is skipped automatically if
				// the column is already present.
				`create table if not exists buoy_stations (
					station_id text primary key,
					name text not null,
					lat real not null,
					lon real not null,
					type text,
					owner text,
					is_drifting integer not null default 0,
					pgm text,
					met integer not null default 0,
					currents integer not null default 0,
					water_quality integer not null default 0,
					dart integer not null default 0
				)`,
				`alter table buoy_stations add column buoycam integer not null default 0`,
			],
		},
		{
			version: 3,
			name: 'buoy_ndbc_feeds',
			description: 'Buoy source tagging plus the NDBC spectral partition and DART water-column columns',
			up: [
				// Same guard as v2: the tables may not exist yet on a fresh install,
				// and each alter is skipped automatically if the column is present
				`create table if not exists buoy_stations (
					station_id text primary key,
					name text not null,
					lat real not null,
					lon real not null,
					type text,
					owner text,
					is_drifting integer not null default 0,
					pgm text,
					met integer not null default 0,
					currents integer not null default 0,
					water_quality integer not null default 0,
					dart integer not null default 0,
					buoycam integer not null default 0
				)`,
				`alter table buoy_stations add column source text not null default 'ndbc'`,
				`alter table buoy_stations add column wmo_id text`,
				`create table if not exists buoy_observations (
					station_id text not null,
					time text not null,
					wind_direction real,
					wind_speed real,
					wind_gust real,
					air_temp real,
					pressure real,
					pressure_tendency real,
					visibility real,
					dewpoint real,
					wave_height real,
					wave_period real,
					avg_wave_period real,
					wave_direction real,
					water_temp real,
					lat real,
					lon real,
					primary key (station_id, time)
				)`,
				`alter table buoy_observations add column swell_height real`,
				`alter table buoy_observations add column swell_period real`,
				`alter table buoy_observations add column swell_direction real`,
				`alter table buoy_observations add column wind_wave_height real`,
				`alter table buoy_observations add column wind_wave_period real`,
				`alter table buoy_observations add column wind_wave_direction real`,
				`alter table buoy_observations add column steepness text`,
				`alter table buoy_observations add column water_column_height real`,
				`alter table buoy_observations add column dart_measurement_type integer`,
			],
		},
		{
			version: 4,
			name: 'buoy_coops',
			description: 'CO-OPS station mapping and tide-gauge water level',
			up: [
				`alter table buoy_stations add column coops_id text`,
				`alter table buoy_observations add column water_level real`,
			],
		},
		// Add new migrations here with incrementing version numbers
	]

	/**
	 * Initialize migrations table and run pending migrations
	 */
	public static async initialize(db: Database): Promise<void> {
		try {
			await this.createMigrationsTable(db)
			await this.runPendingMigrations(db)
		} catch (error) {
			logger.error('Failed to initialize database migrations:', error)
			throw error
		}
	}

	/**
	 * Create the migrations tracking table
	 */
	private static async createMigrationsTable(db: Database): Promise<void> {
		const sql = `
			CREATE TABLE IF NOT EXISTS ${this.MIGRATIONS_TABLE} (
				version INTEGER PRIMARY KEY,
				name TEXT NOT NULL,
				description TEXT,
				applied_at TEXT NOT NULL,
				execution_time_ms INTEGER
			)
		`
		await db.exec(sql)
	}

	/**
	 * Get list of applied migrations
	 */
	private static async getAppliedMigrations(db: Database): Promise<Set<number>> {
		try {
			const result = await db.exec(
				`SELECT version FROM ${this.MIGRATIONS_TABLE} ORDER BY version`
			)
			return new Set((result as MigrationRow[]).map(row => row.version))
		} catch {
			// Table might not exist yet
			return new Set()
		}
	}

	/**
	 * Check if a column exists in a table
	 */
	private static async columnExists(db: Database, table: string, column: string): Promise<boolean> {
		try {
			const result = await db.exec(`PRAGMA table_info(${table})`)
			return (result as { name: string }[]).some(row => row.name === column)
		} catch {
			return false
		}
	}

	/**
	 * Run all pending migrations
	 */
	private static async runPendingMigrations(db: Database): Promise<void> {
		const applied = await this.getAppliedMigrations(db)
		const pending = this.migrations.filter(m => !applied.has(m.version))

		if (pending.length === 0) {
			// logger.info('Database schema is up to date')
			return
		}

		logger.info(`Running ${pending.length} pending migration(s)`)

		for (const migration of pending) {
			await this.applyMigration(db, migration)
		}

		logger.info('All migrations completed successfully')
	}

	/**
	 * Apply a single migration
	 */
	private static async applyMigration(db: Database, migration: Migration): Promise<void> {
		const startTime = Date.now()

		try {
			logger.info(`Applying migration ${migration.version}: ${migration.name}`)

			// Run all statements in a transaction so a partially-applied migration
			// (e.g. a table rebuild that drops the old table) rolls back cleanly on
			// failure instead of leaving the schema in a broken state.
			await db.exec('begin transaction')
			try {
				// Special handling for ALTER TABLE ADD COLUMN
				// SQLite will error if column already exists
				for (const statement of migration.up) {
					const alterTableMatch = /ALTER TABLE (\w+) ADD COLUMN (\w+)/i.exec(statement)

					if (alterTableMatch) {
						const [, table, column] = alterTableMatch
						const exists = await this.columnExists(db, table, column)

						if (exists) {
							logger.info(`Column ${column} already exists in ${table}, skipping`)
							continue
						}
					}

					// Execute the SQL statement
					await db.exec(statement)
				}
				await db.exec('commit')
			} catch (txError) {
				await db.exec('rollback')
				throw txError
			}

			// Record the migration
			const executionTime = Date.now() - startTime
			await db.exec(
				`INSERT INTO ${this.MIGRATIONS_TABLE}
				(version, name, description, applied_at, execution_time_ms)
				VALUES (?, ?, ?, ?, ?)`,
				[
					migration.version,
					migration.name,
					migration.description,
					new Date().toISOString(),
					executionTime
				]
			)

			logger.info(`Migration ${migration.version} completed in ${executionTime}ms`)
		} catch (error) {
			logger.error(`Migration ${migration.version} failed:`, error)
			throw new Error(`Failed to apply migration ${migration.version}: ${String(error)}`)
		}
	}

	/**
	 * Get migration status for debugging
	 */
	public static async getStatus(db: Database): Promise<{
		appliedCount: number
		pendingCount: number
		lastApplied?: {
			version: number
			name: string
			appliedAt: string
		}
	}> {
		const applied = await this.getAppliedMigrations(db)
		const pending = this.migrations.filter(m => !applied.has(m.version))

		let lastApplied
		if (applied.size > 0) {
			const lastVersion = Math.max(...Array.from(applied))
			const result = await db.exec(
				`SELECT version, name, applied_at FROM ${this.MIGRATIONS_TABLE} WHERE version = ?`,
				[lastVersion]
			) as MigrationRow[]
			if (result && result.length > 0) {
				const row = result[0]
				lastApplied = {
					version: row.version,
					name: row.name,
					appliedAt: row.applied_at
				}
			}
		}

		return {
			appliedCount: applied.size,
			pendingCount: pending.length,
			lastApplied
		}
	}

	/**
	 * Get list of all migrations with their status
	 */
	public static async listMigrations(db: Database): Promise<Array<{
		version: number
		name: string
		description: string
		applied: boolean
		appliedAt?: string
	}>> {
		const applied = await this.getAppliedMigrations(db)
		const appliedData = new Map<number, string>()

		try {
			const result = await db.exec(
				`SELECT version, applied_at FROM ${this.MIGRATIONS_TABLE}`
			)
			for (const row of result as MigrationRow[]) {
				appliedData.set(row.version, row.applied_at)
			}
		} catch {
			// Migrations table might not exist yet
		}

		return this.migrations.map(m => ({
			version: m.version,
			name: m.name,
			description: m.description,
			applied: applied.has(m.version),
			appliedAt: appliedData.get(m.version)
		}))
	}
}

